import React, { useEffect, useMemo, useState } from "react";

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

async function readJson(response, fallbackMessage) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || fallbackMessage);
  return data;
}

async function fetchEnterpriseClientData(apiBase, token, path) {
  const response = await fetch(`${apiBase}/admin/client-sync/enterprise/${path}`, {
    method: "GET",
    headers: authHeaders(token),
  });
  return readJson(response, "클라이언트 동기화 데이터를 불러오지 못했습니다.");
}

async function queueEnterpriseClientCommand(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/admin/client-sync/enterprise/commands`, {
    method: "POST",
    headers: { ...authHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return readJson(response, "클라이언트 명령을 등록하지 못했습니다.");
}

async function runEnterpriseClientSelfTest(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/client-sync/enterprise/self-test`, {
    method: "POST",
    headers: authHeaders(token),
  });
  return readJson(response, "테스트 데이터를 생성하지 못했습니다.");
}

function display(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function EnterpriseClientSyncPanel({ apiBase, token, isSuperAdmin }) {
  const [summary, setSummary] = useState(null);
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [command, setCommand] = useState({
    tenant_id: "",
    company: "",
    user_id: "",
    device_id: "",
    command_type: "message",
    title: "서버 메시지",
    message: "",
  });

  const totals = useMemo(() => ({
    devices: summary?.devices || 0,
    events: summary?.events || 0,
    learning: summary?.learning_bundles || 0,
    commands: summary?.commands || 0,
  }), [summary]);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryData, deviceData, eventData, bundleData, commandData] = await Promise.all([
        fetchEnterpriseClientData(apiBase, token, "summary"),
        fetchEnterpriseClientData(apiBase, token, "devices?limit=80"),
        fetchEnterpriseClientData(apiBase, token, "events?limit=80"),
        fetchEnterpriseClientData(apiBase, token, "learning-bundles?limit=80"),
        fetchEnterpriseClientData(apiBase, token, "commands?limit=80"),
      ]);
      setSummary(summaryData);
      setDevices(deviceData.items || []);
      setEvents(eventData.items || []);
      setBundles(bundleData.items || []);
      setCommands(commandData.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelfTest = async () => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const result = await runEnterpriseClientSelfTest(apiBase, token);
      setNotice(`테스트 데이터 생성 완료: 장치 ${result.device_id}, 이벤트 ${result.event_id}, 학습 ${result.learning_bundle_id}, 명령 ${result.command_id}`);
      await loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQueueCommand = async () => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        tenant_id: command.tenant_id.trim(),
        company: command.company.trim(),
        user_id: command.user_id.trim(),
        device_id: command.device_id.trim(),
        command_type: command.command_type,
        title: command.title.trim() || "서버 메시지",
        payload: {
          message: command.message.trim(),
          source: "admin-console",
        },
      };
      if (!payload.payload.message) throw new Error("클라이언트에 보낼 메시지를 입력해주세요.");
      const result = await queueEnterpriseClientCommand(apiBase, token, payload);
      setNotice(`명령 큐에 등록했습니다. ID: ${result.command_id}`);
      setCommand((prev) => ({ ...prev, message: "" }));
      await loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="dashboard-panel">
      <div className="panel-heading">
        <div>
          <p className="panel-kicker">CLIENT SYNC</p>
          <h3>기업 클라이언트 동기화</h3>
          <p>기업회원 PC와 모바일 독립 AI가 서버로 올린 장치, 이벤트, 학습 번들, 명령 큐를 확인합니다.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="admin-approve-button" onClick={handleSelfTest} disabled={loading}>
            테스트 데이터 생성
          </button>
          <button type="button" className="admin-secondary-button" onClick={loadAll} disabled={loading}>
            {loading ? "불러오는 중" : "새로고침"}
          </button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {notice && <div className="admin-success">{notice}</div>}

      <div className="admin-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
        <article className="admin-stat-card">
          <span>등록 장치</span>
          <strong>{totals.devices}</strong>
        </article>
        <article className="admin-stat-card">
          <span>수신 이벤트</span>
          <strong>{totals.events}</strong>
        </article>
        <article className="admin-stat-card">
          <span>학습 번들</span>
          <strong>{totals.learning}</strong>
        </article>
        <article className="admin-stat-card">
          <span>명령 큐</span>
          <strong>{totals.commands}</strong>
        </article>
      </div>

      <div className="dashboard-panel" style={{ marginTop: 16 }}>
        <div className="panel-heading">
          <div>
            <p className="panel-kicker">COMMAND</p>
            <h3>클라이언트 명령 등록</h3>
            <p>대상 회사, 사용자, 장치를 비우면 권한 범위 안에서 공통 명령으로 등록합니다.</p>
          </div>
        </div>
        <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
          {isSuperAdmin && (
            <>
              <label>
                회사 ID
                <input value={command.tenant_id} onChange={(event) => setCommand((prev) => ({ ...prev, tenant_id: event.target.value }))} placeholder="예: tobe-demo" />
              </label>
              <label>
                회사명
                <input value={command.company} onChange={(event) => setCommand((prev) => ({ ...prev, company: event.target.value }))} placeholder="예: 주식회사투비" />
              </label>
            </>
          )}
          <label>
            사용자 ID
            <input value={command.user_id} onChange={(event) => setCommand((prev) => ({ ...prev, user_id: event.target.value }))} placeholder="비우면 회사 공통" />
          </label>
          <label>
            장치 ID
            <input value={command.device_id} onChange={(event) => setCommand((prev) => ({ ...prev, device_id: event.target.value }))} placeholder="비우면 사용자 공통" />
          </label>
          <label>
            명령 제목
            <input value={command.title} onChange={(event) => setCommand((prev) => ({ ...prev, title: event.target.value }))} />
          </label>
          <label style={{ gridColumn: "1 / -1" }}>
            메시지
            <textarea rows={3} value={command.message} onChange={(event) => setCommand((prev) => ({ ...prev, message: event.target.value }))} placeholder="클라이언트에 내려보낼 지시나 안내를 입력하세요." />
          </label>
        </div>
        <button type="button" className="admin-approve-button" onClick={handleQueueCommand} disabled={loading}>
          명령 등록
        </button>
      </div>

      <DataTable
        title="등록 장치"
        columns={["회사", "사용자", "장치", "종류", "플랫폼", "로컬 모델", "최근 접속"]}
        rows={devices.map((item) => [
          item.company || item.tenant_id,
          item.user_name || item.user_id,
          item.client_name || item.device_id,
          item.device_type,
          item.platform,
          item.local_model,
          item.last_seen_at,
        ])}
      />

      <DataTable
        title="최근 이벤트"
        columns={["ID", "회사", "사용자", "장치", "유형", "수신 시각"]}
        rows={events.map((item) => [
          item.id,
          item.company || item.tenant_id,
          item.user_id,
          item.device_id,
          item.event_type,
          item.created_at,
        ])}
      />

      <DataTable
        title="학습 번들"
        columns={["ID", "회사", "사용자", "장치", "유형", "요약", "상태"]}
        rows={bundles.map((item) => [
          item.id,
          item.company || item.tenant_id,
          item.user_name || item.user_id,
          item.device_id,
          item.bundle_type,
          item.summary,
          item.status,
        ])}
      />

      <DataTable
        title="명령 큐"
        columns={["ID", "회사", "사용자", "장치", "유형", "제목", "상태"]}
        rows={commands.map((item) => [
          item.id,
          item.company || item.tenant_id,
          item.user_id || "공통",
          item.device_id || "공통",
          item.command_type,
          item.title,
          item.status,
        ])}
      />
    </section>
  );
}

function DataTable({ title, columns, rows }) {
  return (
    <div className="dashboard-panel" style={{ marginTop: 16 }}>
      <div className="panel-heading">
        <div>
          <p className="panel-kicker">SYNC DATA</p>
          <h3>{title}</h3>
        </div>
      </div>
      {!rows.length ? (
        <p>표시할 데이터가 없습니다.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                {columns.map((column) => <th key={column}>{column}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`${title}-${rowIndex}`}>
                  {row.map((value, columnIndex) => <td key={`${title}-${rowIndex}-${columnIndex}`}>{display(value)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default EnterpriseClientSyncPanel;
